import { Notify } from "@digitallab/grid-common-frontend-components";
import gql from "graphql-tag";
import { WithApolloClient } from "react-apollo";
import { InstrumentDetailsForStepperModelType } from "./StepperContainer";
import { History } from "history";
import { sterilizeObjectList } from "../utils/helpers/sterilizeObject";
import { APP_PATHS, entryType, equipmentStatus } from "../constants";

export const isSelectableEquipment = (equipment: InstrumentDetailsForStepperModelType) =>
  equipment.status === equipmentStatus.active.key &&
  equipment.entryType === entryType.standaloneEquipment;

export const loadEquipmentsData = async (
  equipmentIdList: string[],
  client: WithApolloClient<any>,
  history: History<unknown>,
  pathname: string
) => {
  const queryString = equipmentIdList
    .map(
      (_, index) => `
  input${index}: getDigitalLabLogbookInstrument(inventoryId: $input${index}) {
     belongingToGroup,
     configurationDetails,
     configurationBaseline,
     softwareVersion,
     equipmentId,
     equipmentModel,
     equipmentNickName,
     inventoryId,
     location,
     qualificationStatus,
     serialNumber,
     siteName,
     status,
     systemStatus,
     entryType,
     gxpRelevant,
     linkedInstance,
  }
`
    )
    .join("\n");
  const output = ` query BulkGetDigitalLabLogbookInstrument(
      ${equipmentIdList.map((_, index) => `$input${index}: String!`).join(", ")}
    ) {
      ${queryString}
    }`;

  const variables = Object.fromEntries(equipmentIdList.map((equipment, index) => [`input${index}`, equipment]));

  try {
    const result = await client.query({
      query: gql(output),
      fetchPolicy: "no-cache",
      variables
    });
    // as we send bulk request - return data are in shape {input0:{...equipmentDetails},input1:{...nextEqupmentDetails}}
    if (Object.values(result.data)) {
      const foundEquipment = Object.values(result.data as { input: InstrumentDetailsForStepperModelType }).filter(
        Boolean
      );

      if (foundEquipment.length < equipmentIdList.length) {
        const foundEquipmentIds = foundEquipment.map(({ inventoryId }) => inventoryId);

        const removedInventoryIds = equipmentIdList.filter((item) => !foundEquipmentIds.includes(item));
        sessionStorage.setItem("equipmentIdList", JSON.stringify(foundEquipmentIds));
        throw new Error(`Unable to find equipment with following id(s): ${removedInventoryIds.join(", ")}`);
      }

      const ineligibleEquipment = foundEquipment.filter((item) => !isSelectableEquipment(item));
      if (ineligibleEquipment.length > 0) {
        const ineligibleIds = ineligibleEquipment.map(({ inventoryId }) => inventoryId);
        Notify({
          title: "",
          text: `Cannot create logs for inactive or non-standalone equipment: ${ineligibleIds.join(", ")}`,
          type: "warning",
          icon: "alarm"
        });
        const selectableEquipment = foundEquipment.filter(isSelectableEquipment);
        if (selectableEquipment.length === 0) {
          history.push(APP_PATHS.EQUIPMENT_LIST);
          return [];
        }
        sessionStorage.setItem(
          "equipmentIdList",
          JSON.stringify(selectableEquipment.map(({ inventoryId }) => inventoryId))
        );
        return sterilizeObjectList(selectableEquipment);
      }

      return sterilizeObjectList(foundEquipment);
    } else {
      throw new Error(`Did not received any data in response`);
    }
  } catch (error) {
    Notify({
      title: "Error when loading equipment(s) details",
      text: (error as Error).message,
      type: "warning",
      icon: "alarm"
    });
  }
  return [];
};
